import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';
import { LISTING_TYPE_BUSINESS } from '../../util/types';
import { denormalisedResponseEntities } from '../../util/data';
import { fetchNewCategories } from '../../util/api';

const { UUID } = sdkTypes;

// ================ Async Thunks ================ //

export const uploadImageThunk = createAsyncThunk(
  'companyProfile/uploadImage',
  async (actionPayload, { extra: sdk, rejectWithValue }) => {
    const { id, file } = actionPayload;
    
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    try {
      const response = await sdk.images.upload(bodyParams, queryParams);
      const uploadedImage = response.data.data;
      return { id, uploadedImage };
    } catch (e) {
      return rejectWithValue({ id, error: storableError(e) });
    }
  }
);

export const updateProfileThunk = createAsyncThunk(
  'companyProfile/updateProfile',
  async (actionPayload, { extra: sdk, dispatch, getState, rejectWithValue }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage', 'stripeAccount'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    try {
      const state = getState();
      const currentUser = state.user?.currentUser;
      const existingProfile = currentUser?.attributes?.profile || {};
      const existingPublicData = existingProfile?.publicData || {};
      const existingProtectedData = existingProfile?.protectedData || {};
      const existingBusinessListingId = existingProtectedData?.businessListingId;
      const existingBusinessListingPublished = existingProtectedData?.businessListingPublished;

      const publicData = actionPayload?.publicData || {};
      const title = publicData.companyName || existingPublicData.companyName || 'Business listing';

      const profileImageIdRaw = actionPayload?.profileImageId;
      const profileImageId =
        profileImageIdRaw && profileImageIdRaw.uuid
          ? profileImageIdRaw
          : profileImageIdRaw
            ? new UUID(profileImageIdRaw)
            : null;
      const listingImages = {};

      // Keep listing publicData in sync with profile publicData.
      const listingPublicData = {
        ...publicData,
        typeOfListing: LISTING_TYPE_BUSINESS,
        tag: 'pending',
        unitType: 'item',
        isAdminApproved: true,
        listingType: 'default-accept-bid',
        transactionProcessAlias: 'default-accept-bid/release-1',
      };

      const listingQueryParams = { expand: true, include: ['author', 'images'] };

      // 1) Update or create listing
      const listingResponse = existingBusinessListingId
        ? await sdk.ownListings.update(
            {
              id: new UUID(existingBusinessListingId),
              title,
              ...listingImages,
              publicData: listingPublicData,
            },
            listingQueryParams
          )
        : await sdk.ownListings.create(
            {
              title,
              ...listingImages,
              publicData: listingPublicData,
            },
            listingQueryParams
          );

      dispatch(addMarketplaceEntities(listingResponse));

      const listingIdUuid = listingResponse?.data?.data?.id?.uuid;
      const listingId = listingIdUuid ? new UUID(listingIdUuid) : null;

      // 2) If listing is draft, publish it
      if (listingId && (!existingBusinessListingId || existingBusinessListingPublished === 'draft')) {
        try {
          const publishResponse = await sdk.ownListings.publishDraft({ id: listingId }, { expand: true });
          dispatch(addMarketplaceEntities(publishResponse));
        } catch (e) {
          return rejectWithValue(storableError(e));
        }
      }

      // 3) Update user profile and persist protectedData flags
      const profilePayload = {
        ...actionPayload,
        protectedData: {
          ...existingProtectedData,
          ...(actionPayload?.protectedData || {}),
          businessListingId: listingIdUuid || existingBusinessListingId,
          businessListingPublished: 'published',
          signUpCompleted: 'completed',
          isSignupIncomplete: false,
        },
      };

      const profileResponse = await sdk.currentUser.updateProfile(profilePayload, queryParams);
      const entities = denormalisedResponseEntities(profileResponse);

      const updatedCurrentUser = entities[0];

      // dispatch(addMarketplaceEntities(profileResponse));
      dispatch(currentUserShowSuccess(updatedCurrentUser));
      return updatedCurrentUser;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// fetch new categories code
const requestFetchNewCategoriesExceptionsPayloadCreator = (
  {  },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  return fetchNewCategories({}).then(response => {
    const categories = response?.data || [];
    return categories;
  }).catch(error => rejectWithValue(error));
};

export const requestFetchNewCategoriesThunk = createAsyncThunk(
  'CompanyProfilePage/FetchNewCategories',
  requestFetchNewCategoriesExceptionsPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestFetchNewCategories = params => (dispatch, getState, sdk) => {
  return dispatch(requestFetchNewCategoriesThunk({ params })).unwrap();
};

// ================ Slice ================ //

const companyProfileSlice = createSlice({
  name: 'companyProfile',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
    categories: [], // Add categories to initial state
    fetchCategoriesInProgress: false,
    fetchCategoriesError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload;
      })
      // updateProfile
      .addCase(updateProfileThunk.pending, state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.image = null;
        state.updateInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.image = null;
        state.updateInProgress = false;
        state.updateProfileError = action.payload;
        state.updateInProgress = false;
      })
      // fetchCategories
      .addCase(requestFetchNewCategoriesThunk.pending, state => {
        state.fetchCategoriesInProgress = true;
        state.fetchCategoriesError = null;
      })
      .addCase(requestFetchNewCategoriesThunk.fulfilled, (state, action) => {
        state.categories = action.payload;
        state.fetchCategoriesInProgress = false;
        state.fetchCategoriesError = null;
      })
      .addCase(requestFetchNewCategoriesThunk.rejected, (state, action) => {
        state.fetchCategoriesInProgress = false;
        state.fetchCategoriesError = action.payload;
      });
  },
});

export default companyProfileSlice.reducer;

export const { clearUpdatedForm } = companyProfileSlice.actions;

// ================ Backward Compatible Thunk Wrappers ================ //

export const uploadImage = (actionPayload) => (dispatch, getState, sdk) => {
  return dispatch(uploadImageThunk(actionPayload));
};

export const updateProfile = (actionPayload) => (dispatch, getState, sdk) => {
  return dispatch(updateProfileThunk(actionPayload));
};

export const loadData = () => (dispatch, getState, sdk) =>{
  return Promise.all([
    // Fetch new categories from backend
    dispatch(requestFetchNewCategories()),
  ]);
};